﻿<template>
    <q-dialog ref="dialogRef">
        <q-card class="q-dialog-plugin">
            <q-card-section class="row items-center">
                {{ props.title }}
            </q-card-section>

            <q-card-section class="row items-center">
                <qe-form class="dialog-form">
                    <qe-input v-model="firstName" :label="ts('firstName')" autofocus />
                    <qe-input v-model="lastName" :label="ts('lastName')" />
                    <qe-combo-box
                        v-model="refereeRole"
                        :label="ts('refereeRole')"
                        :options="refereeRolesList"
                    />
                    <qe-combo-box
                        v-model="refereeCourse"
                        :label="ts('course')"
                        :options="coursesList"
                        v-if="refereeRole === RefereeRole.MainCourseReferee && props.useCourses"
                    />
                    <qe-input v-model="wsId" :label="ts('wsId')" />
                    <qe-input v-model="email" type="email" :label="ts('email')" />
                    <qe-input v-model="phone" :label="ts('phone')" />
                    <div class="q-mt-sm">
                        {{ ts('sex') }}
                        <q-option-group
                            v-model="sex"
                            :options="[{ value: 'male', label: ts('male') }, { value: 'female', label: ts('female') }]"
                            inline
                            @keydown.left="selectOtherSex"
                            @keydown.right="selectOtherSex"
                        />
                    </div>
                </qe-form>
            </q-card-section>

            <q-card-actions align="right">
                <q-btn flat :label="ts('close')" color="secondary" @click="onDialogCancel" />
                <q-btn flat :label="props.save" color="primary" @click="onSave" />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script setup lang="ts">
import InfoBox from '@dialogs/InfoBox.vue'
import RefereeRole from '@model/RefereeRole'
import QeComboBox from '@qe/qeComboBox.vue'
import QeForm from '@qe/qeForm.vue'
import QeInput from '@qe/qeInput.vue'
import RefereeRolesRepo from '@repo/RefereeRolesRepo'
import { useRepo } from 'pinia-orm'
import { useDialogPluginComponent, useQuasar } from 'quasar'
import { ts } from '@/plugins/i18nFormat'
import { computed, ref } from 'vue'

interface Props {
    title?: string
    save?: string
    color?: string
    useCourses?: boolean
    coursesList: [{ value: string; label: string }]
}

const props = withDefaults(defineProps<Props>(), {
    title: '',
    color: 'primary',
    save: () => ts('ok'),
    useCourses: false,
    coursesList: [],
})

defineEmits([...useDialogPluginComponent.emits])

const quasar = useQuasar()

const { dialogRef, onDialogOK, onDialogCancel } = useDialogPluginComponent()

const firstName = ref('')
const lastName = ref('')
const wsId = ref('')
const sex = ref('male')
const email = ref('')
const phone = ref('')
const refereeRole = ref(null)
const refereeCourse = ref(null)

const refereeRolesList = computed(() =>
    useRepo(RefereeRolesRepo).orderedList().filter(role => !role.forCoursesOnly || props.useCourses)
)

function selectOtherSex() {
    sex.value = sex.value === 'male' ? 'female' : 'male'
}

function onSave() {
    if (
        firstName.value === undefined || firstName.value.trim().length <= 1 || lastName.value === undefined
        || lastName.value.length <= 1
    ) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                message: ts('missingForeignOfficialRegData'),
            },
        })

        return
    }

    onDialogOK({
        firstName: firstName.value.trim(),
        lastName: lastName.value.trim(),
        wsId: wsId.value.trim(),
        sex: sex.value,
        email: email.value.trim(),
        phone: phone.value.trim(),
        role: refereeRole.value,
        course: refereeCourse.value,
    })
}
</script>

<style scoped lang="sass">
.dialog-form
    width: 100%
</style>
